import { zodResolver } from "@hookform/resolvers/zod";
import { Layers } from "lucide-react";
import { Controller, useForm } from "react-hook-form";
import { useNavigate } from "react-router";
import { z } from "zod";

import { useAuth } from "@/providers";
import { useNotification } from "@/shared/components/notifications/useNotification";
import { useCreateDeviceType } from "@/shared/domain/deviceTypes/hooks";
import {
  Banner,
  RobotoButton,
  TagInput,
  Textarea,
  TextInput,
} from "@/shared/foundation/primitives";
import { userFacingErrorMessage } from "@/shared/http";
import { Goto } from "@/shared/navigation";

import styles from "./CreateDeviceType.module.css";

const deviceTypeFormSchema = z.object({
  // Unique within the org; the backend rejects a collision, which the error
  // banner shows.
  name: z.string().trim().min(1, "Name is required"),
  description: z.string(),
  tags: z.array(z.string()),
});

type DeviceTypeFormValues = z.infer<typeof deviceTypeFormSchema>;

interface CreateDeviceTypeProps {
  close: () => void;
}

/**
 * The New Device Type form: name, description and tags. Metadata is edited on
 * the type's Details tab. Opens the new type's page once it is created.
 */
export function CreateDeviceType({ close }: CreateDeviceTypeProps) {
  const { currentOrganization } = useAuth();
  const showNotification = useNotification();
  const navigate = useNavigate();
  const { mutate: createDeviceType, isPending, error } = useCreateDeviceType();

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<DeviceTypeFormValues>({
    resolver: zodResolver(deviceTypeFormSchema),
    defaultValues: { name: "", description: "", tags: [] },
  });

  const onSubmit = (values: DeviceTypeFormValues) => {
    if (!currentOrganization?.org_id) {
      return;
    }
    const description = values.description.trim();
    createDeviceType(
      {
        orgId: currentOrganization.org_id,
        request: {
          name: values.name.trim(),
          description: description === "" ? undefined : description,
          tags: values.tags,
          metadata: {},
        },
      },
      {
        onSuccess: (deviceType) => {
          close();
          showNotification(`Device type ${deviceType.name} created`);
          void navigate(Goto.createDeviceTypeRoute(deviceType.device_type_id));
        },
      },
    );
  };

  return (
    <div className={styles.panel}>
      <div className={styles.header}>
        <Layers size={14} /> New device type
      </div>

      <form
        className={styles.form}
        onSubmit={(e) => void handleSubmit(onSubmit)(e)}
      >
        {error && (
          <Banner variant="error">{userFacingErrorMessage(error)}</Banner>
        )}

        <div className={styles.field}>
          <label className={styles.fieldLabel} htmlFor="new-device-type-name">
            Name<span className={styles.required}>*</span>
          </label>
          <TextInput
            {...register("name")}
            id="new-device-type-name"
            autoFocus
            autoComplete="off"
            placeholder="e.g. Rover Mk2"
          />
          {errors.name && (
            <span className={styles.fieldError}>{errors.name.message}</span>
          )}
        </div>

        <div className={styles.field}>
          <label
            className={styles.fieldLabel}
            htmlFor="new-device-type-description"
          >
            Description
          </label>
          <Textarea
            {...register("description")}
            id="new-device-type-description"
            rows={2}
          />
        </div>

        <div className={styles.field}>
          <label className={styles.fieldLabel}>Tags</label>
          <Controller
            control={control}
            name="tags"
            render={({ field }) => (
              <TagInput
                tags={field.value}
                onChange={field.onChange}
                aria-label="Tags"
              />
            )}
          />
        </div>

        <div className={styles.footer}>
          <RobotoButton type="button" variant="secondary" onClick={close}>
            Cancel
          </RobotoButton>
          <RobotoButton type="submit" variant="primary" disabled={isPending}>
            {isPending ? "Creating…" : "Create device type"}
          </RobotoButton>
        </div>
      </form>
    </div>
  );
}
